import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  Input,
  output,
  TemplateRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { isEqual } from 'lodash-es';
import { asapScheduler, from, of } from 'rxjs';
import {
  distinctUntilChanged,
  observeOn,
  switchMap,
  tap,
} from 'rxjs/operators';

import { AuthService } from '@plugins/client/auth';
import { AnimationsConfig } from '@plugins/client/directives/animation.directive';
import { Maybe } from '@plugins/shared/types';

import {
  BASE_LINE_HEIGHT_SIZE,
  PICKED_HEIGHT_TO_NAME_RATIO,
  TRANSLATION_KEYS,
} from '../../private/config';
import {
  type AllEntityTypes,
  type SearchTypes,
} from '../../private/utils/types.private';
import { generateCompositeSize } from '../../private/utils/utils';
import { PickedEntitiesService } from '../services';
import { UserDetailsService } from '../services/user-details.service';

const { PICKED } = TRANSLATION_KEYS;

type PlaceholderType = string | TemplateRef<unknown> | undefined;

/** Displays a list of picked entities which can be removed from the list.
 * Registering of picked entities and their removal is managed via the
 * `PickedEntitiesService` (provided by the DI or instantiated by the component itself).
 *
 * The service can be populated _either_ by the parent component _or_ by the `PickedUsers`
 * component itself via the `setIds` input.
 *
 * Example:
 * ```html
 * <!-- Projecting content at the bottom of the list -->
<plugins-picked-users>
  <li>Some content</li>
</plugins-picked-users>

<!-- Passing a TemplateRef as placeholder and a size prop-->
<plugins-picked-users [placeholderItem]="someTemplate" size="50px" />

<ng-template #someTemplate>
  <section>
    <h3>No users are selected</h3>
    <img src="path/to/placeholder/image" />
    <p>Why don't you select some?</p>
  </section>
</ng-template>

<!-- Listen for picked ids changes that are set manually and a bound disabled status -->
<plugins-picked-users [disabled]="disabled" [setIds]="idList" (pickedIdsChange)="someHandler(ids)" />
 * ```
 */
@Component({
  selector: 'plugins-picked-users',
  standalone: false,
  templateUrl: './picked-users.component.html',
  styleUrl: './picked-users.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PickedUsersComponent {
  /** Set user ids manually. Useful for standalone use cases,
   * where the component is part of the `UserSearchAndPick` component
   * the latter provides picked ids via the `PickedEntitiesService`
   *
   * Listen for `pickedIdsChange` Output for changes in the list.
   *
   *  **Warning**: It is discouraged to use the `setIds` while at the same time having the picked entities list being set manually by a parent component. Diverging sources of truth might lead to undesired effects / state overwrites. Either embed the component in the context in which the picked service is populated by the parent, or populate it independently and listend for state changes.
   * */
  // Keeping Input decorator until rxjs-interop is stable.
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() set setIds(ids: string | string[]) {
    this.pickedService.setIds(ids);
  }

  /** Placeholder item to display (string or`TemplateRef`) when no entities are picked */
  readonly placeholderItem = input<Maybe<PlaceholderType>>();
  /** Disable delete action buttons. Defaults to `false` */
  readonly disabled = input<boolean>(false);
  /** Show or hide delete action buttons. Defaults to `true`. */
  readonly showDelete = input(true);
  /** Can be used for fetch optimization or narrowing when making calls to the `UserDetailsService`.
   * Defaults to `all`. */
  readonly searchType = input<SearchTypes>('all');
  /** Controls the size of the displayed entities. */
  readonly size = input<Maybe<string | number>>();
  /** Should entities have a detailed title associated with them? */
  readonly detailedTitle = input<Maybe<boolean>>();
  /** Should entities link to user profile page. Defaults to `true` */
  readonly profileLinksEnabled = input(true);

  /** Emits picked ids each time they change by a removal or a setter action */
  readonly pickedIdsChange = output<string[]>();

  protected userId = inject(AuthService).userToken.sub;
  protected userDetailsService = inject(UserDetailsService);
  protected pickedService =
    inject(PickedEntitiesService, { optional: true }) ??
    new PickedEntitiesService();

  readonly animations: AnimationsConfig = {
    enter: {
      type: ['fade', 'growY', 'slideTop'],
      timingOptions: { duration: 100, fill: 'forwards' },
    },
  };

  readonly labels = PICKED;

  protected readonly calculatedSizes = computed(() => {
    return {
      listItem:
        generateCompositeSize(this.size(), PICKED_HEIGHT_TO_NAME_RATIO) ??
        BASE_LINE_HEIGHT_SIZE,
    };
  });

  protected readonly placeholders = computed(() => {
    const item = this.placeholderItem();
    const isString = typeof item === 'string';
    return {
      text: isString ? item : null,
      template: !isString ? item : null,
    };
  });

  readonly pickedUsersSub = this.pickedService.pickedIds$
    .pipe(
      distinctUntilChanged((prev, curr) => isEqual(prev, curr)),
      tap((ids) => {
        this.pickedIdsChange.emit(ids);
      }),
      observeOn(asapScheduler),
      switchMap((ids) => {
        if (!ids.length) {
          return of([] as AllEntityTypes[]);
        }
        const searchType = this.searchType();
        return from(
          this.userDetailsService.findEntity(
            ids,
            searchType !== 'none' ? searchType : 'all',
          ),
        );
      }),
      tap((entities) => {
        this.pickedService.setEntityValue(entities);
      }),
      takeUntilDestroyed(),
    )
    .subscribe();

  get pickedServiceIds(): string[] {
    return this.pickedService.pickedIds;
  }

  removeUser(id: string): void {
    this.pickedService.removeIds(id);
  }

  protected identityFunction(id: string) {
    return id;
  }
}
